/**
 * StatusBadge — the canonical block for displaying a domain status as a
 * tinted pill. The single component every product uses for inline status
 * indicators.
 *
 *   <StatusBadge domain="application" status="hired" />
 *   <StatusBadge domain="task" status="in_progress" withDot />
 *   <StatusBadge domain="job" status="open" label="Now hiring" />
 *
 * The (domain, status) pair resolves through `DOMAIN_STATUS_INTENT` in
 * `packages/ui/src/tokens/status.ts` → an `Intent` → the Badge variant.
 * Call sites never name colors directly.
 *
 * Optional props:
 *   - withDot: prepend a small colored dot matching the intent.
 *   - label:   override the human label; defaults to the status key with
 *              underscores → spaces.
 *   - className: standard className passthrough.
 */

import * as React from "react";
import { Badge } from "../badge";
import { cn } from "../utils";
import {
  DOMAIN_STATUS_INTENT,
  INTENT_CLASSES,
  type Intent,
  type StatusDomain,
  type StatusOf,
} from "../tokens";

const DEFAULT_LABEL = (status: string) =>
  status.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

type StatusBadgeProps<D extends StatusDomain> = {
  domain: D;
  status: StatusOf<D>;
  withDot?: boolean;
  label?: string;
  className?: string;
};

export function StatusBadge<D extends StatusDomain>({
  domain,
  status,
  withDot = false,
  label,
  className,
}: StatusBadgeProps<D>) {
  const intent =
    (DOMAIN_STATUS_INTENT[domain] as Record<string, Intent>)[status as string] ??
    "neutral";
  const text = label ?? DEFAULT_LABEL(status as string);
  return (
    <Badge variant={intent} className={className}>
      {withDot ? (
        <span
          aria-hidden
          className={cn(
            "inline-block size-1.5 rounded-full",
            INTENT_CLASSES[intent].dot,
          )}
        />
      ) : null}
      {text}
    </Badge>
  );
}

/**
 * IntentBadge — when you have an `Intent` directly (not a domain status),
 * use this. Examples: a generic "Success" toast pill, a warning notice in
 * settings, an arbitrary danger flag with no enum behind it.
 */
export function IntentBadge({
  intent,
  withDot = false,
  className,
  children,
}: {
  intent: Intent;
  withDot?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Badge variant={intent} className={className}>
      {withDot ? (
        <span
          aria-hidden
          className={cn(
            "inline-block size-1.5 rounded-full",
            INTENT_CLASSES[intent].dot,
          )}
        />
      ) : null}
      {children}
    </Badge>
  );
}
